import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Loader2, ShieldX } from "lucide-react";
import { formatApiErrorDetail } from "@/lib/api";
import guestApi, { getGuestSession, clearGuestSession } from "@/lib/guestApi";
import { LiveKitMeetingRoom } from "@/components/meeting/livekit/LiveKitMeetingRoom";

/** Public meeting room for an unauthenticated (no Cloudivo account) attendee who
 * registered and was approved — reads their guest session from sessionStorage
 * (set by `MeetingRegisterPage`) and drops straight into the LiveKit room. */
export default function GuestMeetingRoomPage() {
  const { meetingId } = useParams();
  const navigate = useNavigate();
  const [meeting, setMeeting] = useState(null);
  const [error, setError] = useState(null);
  const [guestToken, setGuestToken] = useState(null);

  useEffect(() => {
    const session = getGuestSession(meetingId);
    if (!session) {
      setError("Your registration session has expired or wasn't found. Please use your registration link again.");
      return;
    }
    setGuestToken(session.token);
    guestApi.get(`/meetings/${meetingId}`).then(({ data }) => setMeeting(data)).catch((e) => {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Could not load this meeting.");
    });
  }, [meetingId]);

  const leave = () => {
    clearGuestSession();
    navigate("/");
  };

  if (error) {
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center gap-4 text-center px-6" data-testid="guest-room-error">
        <ShieldX className="w-10 h-10 text-red-400" />
        <p className="text-slate-300">{error}</p>
      </div>
    );
  }
  if (!meeting || !guestToken) {
    return <div className="min-h-screen bg-slate-950 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-cyan-400" /></div>;
  }

  return <LiveKitMeetingRoom meetingId={meetingId} meeting={meeting} isHost={false} onLeft={leave} guestToken={guestToken} />;
}
